import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faProjectDiagram, faRetweetAlt } from '@fortawesome/pro-duotone-svg-icons';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { Store } from '@ngxs/store';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ExecuteIvr, InitiateNewCallAction, ReQueueCall, StartNewCallAction } from 'src/app/store/samwin/client-phone/client-phone.actions';
import { ReQueueMessage, UpdateSearchValues } from 'src/app/store/samwin/samwin-mail-list/samwin-message-list.actions';
import { SamwinOutboundSkillShortInfoState } from 'src/app/store/samwin/samwin-outbound-skill-shortinfo/samwin-outbound-skill-shortinfo.state';
import { QueueShortInfoRemoteDto, UsersService } from 'src/generated/swagger';
import { Enum, EnumType } from 'ts-jenum';
import { Sccs1ConversationType } from './Sccs1ConversationType';

@Enum<QueueSelectionPurpose>('name')
export class QueueSelectionPurpose extends EnumType<QueueSelectionPurpose>() {
   static readonly REQUEUE = new QueueSelectionPurpose(
      'ReQueue',
      true,
      faRetweetAlt,
      'samwin.queue_selection_dialog.requeue.title',
      'samwin.queue_selection_dialog.requeue.info_text',
      (store, usersService, call) => {
         if (_.isNil(call)) {
            return null;
         }
         if (
            Sccs1ConversationType.valueBySamwinValue(call.type) === Sccs1ConversationType.Call ||
            Sccs1ConversationType.valueBySamwinValue(call.type) === Sccs1ConversationType.Webchat
         ) {
            return usersService.getRequeueTargetsForCalls().pipe(map((x) => x.queues));
         } else if (Sccs1ConversationType.valueBySamwinValue(call.type) === Sccs1ConversationType.RocketChat) {
            return usersService.getRequeueTargetsForChats().pipe(map((x) => x.queues));
         }
         return usersService.getRequeueTargetsForMessages().pipe(map((response) => response.queues));
      },
      (store, queue, call) => {
         if (
            Sccs1ConversationType.valueBySamwinValue(call.type) === Sccs1ConversationType.Call ||
            Sccs1ConversationType.valueBySamwinValue(call.type) === Sccs1ConversationType.Webchat ||
            Sccs1ConversationType.valueBySamwinValue(call.type) === Sccs1ConversationType.RocketChat
         ) {
            return store.dispatch(new ReQueueCall(call.callId, queue.id));
         }
         const result = store.dispatch(new ReQueueMessage(call.callId, queue.id, call.messageId));
         store.dispatch(new UpdateSearchValues());
         return result;
      }
   );

   static readonly EXECUTE_IVR = new QueueSelectionPurpose(
      'ExecuteIvr',
      true,
      faProjectDiagram,
      'samwin.queue_selection_dialog.execute_script.title',
      'samwin.queue_selection_dialog.execute_script.info_text',
      (store, usersService) => {
         return usersService.getExecuteIvrTargets().pipe(map((x) => x.queues));
      },
      (store, queue, call) => {
         return store.dispatch(new ExecuteIvr(call.callId, queue.id));
      }
   );

   static readonly OUTBOUND_SKILL = new QueueSelectionPurpose(
      'OutboundSkill',
      false,
      faStar,
      'samwin.queue_selection_dialog.select_outbound_skill.title',
      'samwin.queue_selection_dialog.select_outbound_skill.info_text',
      (store) => {
         return store.select(SamwinOutboundSkillShortInfoState.getSkills);
      },
      (store, queue, action: StartNewCallAction) => {
         return store.dispatch(new InitiateNewCallAction(action.numberToDial, queue.id));
      }
   );

   private constructor(
      public name: string,
      public requireCall: boolean,
      public titlebarIcon: IconDefinition,
      public titlebarText: string,
      public infoText: string,
      public queueSelectionRequest: (store: Store, usersService: UsersService, contextData: any) => Observable<QueueShortInfoRemoteDto[]>,
      public completeQueueSelection: (store: Store, queue: QueueShortInfoRemoteDto, contextData: any) => void | Observable<any>
   ) {
      super();
   }
}
